import React, { Fragment, useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'

import { Dropdown, OverlayTrigger, Popover } from 'react-bootstrap'
import Select from 'react-select'
import DatePicker from "react-datepicker"
import { parseISO } from 'date-fns'
import XLSX from 'xlsx-js-style'

import FilterSearch from 'pages/parts/FilterSearch'
import useDidMountEffect from 'pages/parts/useDidMountEffect'

import { GET } from 'helpers/api'
import { formatDate, formatDateBackend, formatMoney } from 'helpers/helpers'
import { styleExcelSheet } from 'helpers/excel'

import './style.css'

// columns order in the cards, popovers and table rows
const CURRENCIES = ['UZS', 'USD']

// PosBalanceTotalDTO.BalanceItem.currencyId: 1 = so'm, 2 = USD (as on the cheques/currency pages)
const CURRENCY_IDS = { 1: 'UZS', 2: 'USD' }

// BalanceItem.paymentTypeId: 1 = cash, the rest (terminal...) is cashless
const CASH_PAYMENT_TYPE_ID = 1

const initialTotal = {
	safeBalance: [],
	cashboxBalance: [],
	safePendingBalance: [],
}

const ACCOUNT_ICONS = {
	safe: 'uil-university',
	cashbox: 'uil-money-withdrawal',
}

const initialFilter = {
	date: formatDateBackend(new Date()),
	posId: null,
	search: '',
}

function currencyLabel(t, currency) {
	return currency === 'UZS' ? t('SUM') : currency
}

// sum of { [currency]: { cash, cashless } } objects, null items are skipped
function sumAmounts(list) {
	const result = {}
	CURRENCIES.forEach(currency => {
		result[currency] = { cash: 0, cashless: 0 }
		list.forEach(amounts => {
			result[currency].cash += Number(amounts?.[currency]?.cash) || 0
			result[currency].cashless += Number(amounts?.[currency]?.cashless) || 0
		})
	})
	return result
}

// BalanceItem[] -> { [currency]: { cash, cashless } }, unknown currencies are skipped
function balanceItemsToAmounts(items) {
	const result = sumAmounts([])
	const list = items || []
	list.forEach(item => {
		const currency = CURRENCY_IDS[item.currencyId]
		if (currency) {
			result[currency][item.paymentTypeId === CASH_PAYMENT_TYPE_ID ? 'cash' : 'cashless'] += Number(item.amount) || 0
		}
	})
	return result
}

// ReportSafeBalanceAmount[] -> { balances, pending }: { [currency]: { cash, cashless } }, unknown currencies are skipped
function reportAmounts(items) {
	const balances = sumAmounts([])
	const pending = sumAmounts([])
	const list = items || []
	list.forEach(item => {
		const currency = CURRENCY_IDS[item.currencyId]
		if (currency) {
			balances[currency].cash += Number(item.cashAmount) || 0
			balances[currency].cashless += Number(item.nonCashAmount) || 0
			pending[currency].cash += Number(item.pendingCashAmount) || 0
			pending[currency].cashless += Number(item.pendingNonCashAmount) || 0
		}
	})
	return { balances, pending }
}

// ReportSafeBalancePos -> point with its safe and cashboxes as accounts
function reportPosToRow(t, pos) {
	const cashboxes = pos.cashboxes || []
	return {
		posId: pos.posId,
		posName: pos.posName,
		accounts: [
			{ id: 'safe', type: 'safe', name: t('SAFE'), ...reportAmounts(pos.safeAmounts) },
			...cashboxes.map(cashbox => ({ id: cashbox.cashboxId, type: 'cashbox', name: cashbox.cashboxName, ...reportAmounts(cashbox.amounts) })),
		],
	}
}

// currencies that have any amount
function usedCurrencies(amounts) {
	return CURRENCIES.filter(currency => amounts[currency].cash !== 0 || amounts[currency].cashless !== 0)
}

function formatValue(value, sign = '') {
	return value ? sign + formatMoney(value, 2) : '—'
}

// content is wrapped so the account rows can animate their height when a point opens / closes
function Cell({ className, children }) {
	return (
		<td className={className}>
			<div className="ab-cell"><div>{children}</div></div>
		</td>
	)
}

// pending ("Ожидает приёма") goes under the balances in the same cell, so the account reads as one block
function AmountCells({ amounts, pending }) {
	const { t } = useTranslation()

	const pendingCurrencies = pending ? usedCurrencies(pending) : []

	const columns = [
		{ key: 'cash', value: (item) => item.cash },
		{ key: 'cashless', value: (item) => item.cashless },
		{ key: 'total', value: (item) => item.cash + item.cashless, className: 'ab-total' },
	]

	// amount | currency grid: amounts and currencies stay aligned between the lines
	function renderLines(source, currencies, getValue, sign, empty) {
		return (
			<div className="ab-amounts">
				{currencies.map(currency => {
					const value = getValue(source[currency])
					return (
						<Fragment key={currency}>
							<span>{value ? sign + formatMoney(value, 2) : empty}</span>
							<span className="ab-currency">{value ? currencyLabel(t, currency) : ''}</span>
						</Fragment>
					)
				})}
			</div>
		)
	}

	return (
		<>
			{columns.map(column => (
				<Cell key={column.key} className={'text-end text-nowrap ' + (column.className || '')}>
					{renderLines(amounts, CURRENCIES, column.value, '', '—')}
					{pendingCurrencies.length > 0 &&
						<div className="ab-pending">
							{renderLines(pending, pendingCurrencies, column.value, '+ ', ' ')}
						</div>
					}
				</Cell>
			))}
		</>
	)
}

function SummaryCard({ id, title, icon, amounts, variant }) {
	const { t } = useTranslation()

	const totals = CURRENCIES.map(currency => ({
		currency,
		value: amounts[currency].cash + amounts[currency].cashless,
	}))

	const popover = (
		<Popover id={`ab-popover-${id}`} className="ab-popover">
			<Popover.Body>
				<div className="ab-popover-title">{title}</div>
				<table className="ab-popover-table">
					<thead>
						<tr>
							<th></th>
							{CURRENCIES.map(currency => <th key={currency} className="text-end">{currencyLabel(t, currency)}</th>)}
						</tr>
					</thead>
					<tbody>
						{['cash', 'cashless'].map(type => (
							<tr key={type}>
								<td>
									<span className={`ab-marker ab-marker-${type}`}></span>
									{t(type === 'cash' ? 'CASH' : 'CASHLESS')}
								</td>
								{CURRENCIES.map(currency => <td key={currency} className="text-end">{formatValue(amounts[currency][type])}</td>)}
							</tr>
						))}
					</tbody>
					<tfoot>
						<tr>
							<td>{t('GRAND_TOTAL')}</td>
							{totals.map(total => <td key={total.currency} className="text-end">{formatValue(total.value)}</td>)}
						</tr>
					</tfoot>
				</table>
			</Popover.Body>
		</Popover>
	)

	return (
		<OverlayTrigger trigger={['hover', 'focus']} placement="bottom-start" overlay={popover}>
			<div className={`ab-card ab-card-${variant}`} tabIndex={0}>
				<div className="ab-card-title">
					{icon && <i className={`uil ${icon}`}></i>}
					{title}
				</div>
				<i className="uil uil-info-circle ab-card-info"></i>
				<div className="ab-card-amounts">
					{totals.map(total => (
						<Fragment key={total.currency}>
							<span className="text-end">{formatMoney(total.value, 2)}</span>
							<span>{currencyLabel(t, total.currency)}</span>
						</Fragment>
					))}
				</div>
			</div>
		</OverlayTrigger>
	)
}

function Index() {
	const { t } = useTranslation()

	const [dropdownIsOpen, setDropdownIsOpen] = useState(false)
	const [filterDataExist, setFilterDataExist] = useState(false)
	const [filterData, setFilterData] = useState({ ...initialFilter, random: 0 })
	const [data, setData] = useState([])
	const [total, setTotal] = useState(initialTotal)
	const [poses, setPoses] = useState([])
	const [openPosId, setOpenPosId] = useState(null)

	const posOptions = poses.map(pos => ({ value: pos.id, label: pos.name }))

	const rows = data.map(pos => reportPosToRow(t, pos))

	// cards follow the date and POS filters, not the search
	const safe = balanceItemsToAmounts(total.safeBalance)
	const cashbox = balanceItemsToAmounts(total.cashboxBalance)
	const summary = {
		total: sumAmounts([safe, cashbox]),
		safe,
		cashbox,
		pending: balanceItemsToAmounts(total.safePendingBalance),
	}

	async function getData() {
		checkFilter(filterData)
		setDropdownIsOpen(false)
		getTotal()
		const response = await GET('/services/web/api/reports-safe-balance', { posId: filterData.posId, date: filterData.date, search: filterData.search }, { loader: true })
		setData(response || [])
	}

	async function getTotal() {
		const response = await GET('/services/web/api/report-pos-balance-total', { posId: filterData.posId, endDate: filterData.date })
		setTotal(response || initialTotal)
	}

	async function getPoses() {
		const response = await GET('/services/web/api/pos-helper')
		if (response) {
			setPoses(response)
		}
	}

	function posName(posId) {
		return posOptions.find(option => option.value === posId)?.label
	}

	function applyFilter() {
		setFilterData({ ...filterData, random: filterData.random + 1 })
	}

	function checkFilter(filter) {
		setFilterDataExist(Object.keys(initialFilter).some(key => filter[key] !== initialFilter[key]))
	}

	function resetFilter() {
		setFilterData({ ...initialFilter, random: filterData.random + 1 })
	}

	// only one point is open at a time
	function toggle(posId) {
		setOpenPosId(openPosId === posId ? null : posId)
	}

	function exportToExcel() {
		const sheet = [[
			t('POS'), t('ACCOUNT'), t('CURRENCY'), t('CASH'), t('CASHLESS'), t('GRAND_TOTAL'),
			`${t('PENDING_ACCEPTANCE')} (${t('CASH')})`, `${t('PENDING_ACCEPTANCE')} (${t('CASHLESS')})`,
		]]
		rows.forEach(pos => {
			pos.accounts.forEach(account => {
				const balances = sumAmounts([account.balances])
				const pending = sumAmounts([account.pending])
				CURRENCIES.forEach(currency => {
					const { cash, cashless } = balances[currency]
					sheet.push([
						pos.posName, account.name, currencyLabel(t, currency), cash, cashless, cash + cashless,
						pending[currency].cash, pending[currency].cashless,
					])
				})
			})
		})
		const ws = XLSX.utils.aoa_to_sheet(sheet)
		styleExcelSheet(ws)
		const wb = XLSX.utils.book_new()
		XLSX.utils.book_append_sheet(wb, ws, "SheetJS")
		XLSX.writeFile(wb, `${t('ACCOUNT_BALANCES')} ${formatDate(parseISO(filterData.date))}.xlsx`)
	}

	useEffect(() => {
		getPoses()
		getData()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	useDidMountEffect(() => {
		getData()
	}, [filterData.random]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex mb-2">
				<h4 className="vertical-center">
					{t('ACCOUNT_BALANCES')}
				</h4>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="d-flex flex-wrap mb-3">
						<div>
							<FilterSearch
								filterData={filterData}
								setFilterData={setFilterData}
								reduxFilterSearch={true}
								disableGroupSearch={true}>
							</FilterSearch>
						</div>
						<div>
							<Dropdown onToggle={() => setDropdownIsOpen(!dropdownIsOpen)} show={dropdownIsOpen}>
								<Dropdown.Toggle variant="outline-primary" className="btn-wide h-100 me-2">
									{t('FILTER')}
								</Dropdown.Toggle>

								<Dropdown.Menu className="dropdown-filter">
									<div className="row">
										<div className="form-group col-md-6">
											<label>{t('DATE')}</label>
											<div className="calendar-input">
												<DatePicker
													className="form-control"
													dateFormat="dd.MM.yyyy"
													maxDate={new Date()}
													selected={filterData.date ? parseISO(filterData.date) : ''}
													onChange={(date) => setFilterData({ ...filterData, 'date': date ? formatDateBackend(date) : initialFilter.date })} />
												<i className="uil uil-calendar-alt"></i>
											</div>
										</div>
										<div className="form-group col-md-6">
											<label>{t('POS')}</label>
											<Select
												options={posOptions}
												value={posOptions.find(option => option.value === filterData.posId) || ''}
												onChange={(option) => setFilterData({ ...filterData, 'posId': option ? option.value : null })}
												placeholder={t('ALL')}
												isClearable
												noOptionsMessage={() => t('LIST_IS_EMPTY')}
											/>
										</div>
									</div>
									<div className="row mt-3">
										<div className="col-md-12">
											<div className="d-flex justify-content-end">
												<button type="button" className="btn btn-secondary me-2" onClick={() => setDropdownIsOpen(false)}>{t('CANCEL')}</button>
												<button type="button" className="btn btn-primary" onClick={() => applyFilter()}>{t('APPLY')}</button>
											</div>
										</div>
									</div>
								</Dropdown.Menu>
							</Dropdown>
						</div>
						<div>
							<button type="button" className="btn btn-outline-primary btn-wide h-100" onClick={exportToExcel}>EXCEL</button>
						</div>
					</div>

					{filterDataExist &&
						<div className="filter-block mb-3">
							<div className="filter">
								<strong>{t('FILTER')}</strong>
								<br />
								<span className="me-2">
									<b>{t('DATE')}: </b>
									{formatDate(parseISO(filterData.date))}
								</span>
								{filterData.posId &&
									<span className="me-2">
										<b>{t('POS')}: </b>
										{posName(filterData.posId)}
									</span>
								}
								{filterData.search &&
									<span className="me-2">
										<b>{t('SEARCH')}: </b>
										<span>{filterData.search}</span>
									</span>
								}
							</div>
							<div className="vertical-center">
								<button className="btn btn-outline-dark text-uppercase" onClick={resetFilter}>{t('RESET')}</button>
							</div>
						</div>
					}

					<div className="ab-cards mb-3">
						<SummaryCard id="total" variant="total" title={t('TOTAL_BALANCE_ALL_POSES')} amounts={summary.total} />
						<SummaryCard id="safe" variant="safe" icon={ACCOUNT_ICONS.safe} title={t('IN_SAFES')} amounts={summary.safe} />
						<SummaryCard id="cashbox" variant="cashbox" icon={ACCOUNT_ICONS.cashbox} title={t('IN_CASHBOXES')} amounts={summary.cashbox} />
						<SummaryCard id="pending" variant="pending" icon="uil-clock-three" title={t('PENDING_ACCEPTANCE')} amounts={summary.pending} />
					</div>

					<div className="table-responsive">
						<table className="table ab-table mb-0">
							<thead>
								<tr>
									<th>{t('POS_ACCOUNT')}</th>
									<th className="text-end">{t('CASH')}</th>
									<th className="text-end">{t('CASHLESS')}</th>
									<th className="text-end">{t('GRAND_TOTAL')}</th>
								</tr>
							</thead>
							{rows.length > 0 ? rows.map((pos, index) => {
								const isOpen = openPosId === pos.posId
								// point's pending is shown on its own row only while it is closed, when open the accounts show it
								const posPending = sumAmounts(pos.accounts.map(account => account.pending))
								const posHasPending = !isOpen && usedCurrencies(posPending).length > 0
								return (
									<tbody key={pos.posId} className={'ab-group' + (isOpen ? ' open' : '')}>
										<tr className="ab-pos-row" onClick={() => toggle(pos.posId)}>
											<Cell>
												<div className={posHasPending ? 'ab-account-main' : ''}>
													<div className="ab-name">
														<i className="uil uil-angle-right ab-chevron"></i>
														{index + 1}. {pos.posName}
													</div>
												</div>
												{posHasPending &&
													<div className="ab-pending ab-pending-label">
														<i className="uil uil-clock-three"></i>
														{t('PENDING_ACCEPTANCE')}
													</div>
												}
											</Cell>
											<AmountCells amounts={sumAmounts(pos.accounts.map(account => account.balances))} pending={posHasPending ? posPending : null} />
										</tr>

										{/* accounts stay rendered and collapse with css, so opening / closing is animated */}
										{pos.accounts.map(account => {
											const accountPending = sumAmounts([account.pending])
											return (
												<tr key={account.id} className="ab-account-row" aria-hidden={!isOpen}>
													<Cell>
														<div className="ab-account-main">
															<div className="ab-name">
																<i className={`uil ${ACCOUNT_ICONS[account.type]} ab-account-icon ab-account-icon-${account.type}`}></i>
																{account.name}
															</div>
														</div>
														{usedCurrencies(accountPending).length > 0 &&
															<div className="ab-pending ab-pending-label">
																<i className="uil uil-clock-three"></i>
																{t('PENDING_ACCEPTANCE')}
															</div>
														}
													</Cell>
													<AmountCells amounts={sumAmounts([account.balances])} pending={accountPending} />
												</tr>
											)
										})}
									</tbody>
								)
							}) :
								<tbody>
									<tr>
										<td colSpan="4" className="text-center">{t('NO_DATA')}</td>
									</tr>
								</tbody>
							}
						</table>
					</div>
				</div>
			</div>
		</>
	)
}

export default Index
